import { useState, useEffect, useRef } from 'react';
import api from '../api';
import { useApp } from '../contexts/AppContext';

interface Conversation {
  id: number;
  title: string | null;
  message_count: number;
  messages?: Array<{ role: string; content: string; timestamp?: string }>;
  updated_at: string | null;
}

export default function AgentChat({ agentId, agentName }: { agentId: string; agentName: string }) {
  const { token } = useApp();
  const [messages, setMessages] = useState<Array<{ role: string; content: string }>>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [pastConversations, setPastConversations] = useState<Conversation[]>([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Load recent conversations on mount
  useEffect(() => {
    if (!token || !agentId) return;
    api.get(`/agent-chat/${agentId}/conversations/recent?limit=5`).then(res => {
      const convs: Conversation[] = res.data.conversations || [];
      setPastConversations(convs);
      // Auto-load the most recent active conversation
      if (convs.length > 0 && convs[0].messages && convs[0].messages.length > 0) {
        setConversationId(convs[0].id);
        setMessages(convs[0].messages.map((m: any) => ({ role: m.role, content: m.content })));
      }
    }).catch(() => {});
  }, [token, agentId]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const sendMessage = async () => {
    if (!input.trim() || !token) return;
    const userMsg = input.trim();
    setInput('');
    setLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    try {
      const res = await api.post(
        `/agent-chat/${agentId}/chat`,
        { message: userMsg, conversation_id: conversationId, context: {} },
      );
      setConversationId(res.data.conversation_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error.' }]);
    }
    setLoading(false);
  };

  const startNewChat = () => {
    // Save current conversation to sidebar if it has messages
    if (conversationId && messages.length > 0) {
      const existing = pastConversations.find(c => c.id === conversationId);
      if (!existing) {
        setPastConversations(prev => [{
          id: conversationId,
          title: messages[0]?.content?.slice(0, 60) || 'Conversation',
          message_count: messages.length,
          updated_at: new Date().toISOString(),
        }, ...prev]);
      }
    }
    setConversationId(null);
    setMessages([]);
  };

  const loadConversation = async (conv: Conversation) => {
    if (!token) return;
    try {
      const res = await api.get(`/agent-chat/conversations/${conv.id}`);
      setConversationId(res.data.id);
      setMessages((res.data.messages || []).map((m: any) => ({ role: m.role, content: m.content })));
      setShowSidebar(false);
    } catch {
      // Conversation may have been deleted
    }
  };

  return (
    <div style={{ display: 'flex', height: '100%' }}>
      {/* Conversation sidebar */}
      {showSidebar && pastConversations.length > 0 && (
        <div style={{ width: 200, borderRight: '1px solid var(--bg-border)', overflowY: 'auto', flexShrink: 0 }}>
          <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--text-dim)', fontWeight: 600, textTransform: 'uppercase' }}>
            Past Chats
          </div>
          {pastConversations.map(c => (
            <div
              key={c.id}
              onClick={() => loadConversation(c)}
              style={{
                padding: '8px 12px', cursor: 'pointer', fontSize: 12,
                color: c.id === conversationId ? 'var(--accent-good)' : 'var(--text-secondary)',
                background: c.id === conversationId ? 'rgba(0,212,170,0.08)' : 'transparent',
                borderBottom: '1px solid rgba(0,0,0,0.04)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {c.title || `Chat #${c.id}`}
              <div style={{ fontSize: 10, color: 'var(--text-dimmer)', marginTop: 2 }}>
                {c.message_count} msgs
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Main chat area */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        {/* Chat header */}
        <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--bg-border)', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
          {pastConversations.length > 0 && (
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 14, padding: '2px 6px' }}
              title="Toggle conversation history"
            >
              {showSidebar ? '\u00AB' : '\u00BB'}
            </button>
          )}
          <span style={{ flex: 1 }}>Chat with {agentName}</span>
          <button
            onClick={startNewChat}
            style={{
              background: 'rgba(0,0,0,0.06)', border: '1px solid var(--bg-border)',
              borderRadius: 6, color: 'var(--text-dim)', cursor: 'pointer', fontSize: 11, padding: '4px 10px',
            }}
          >
            New Chat
          </button>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
          {messages.length === 0 && (
            <p style={{ fontSize: 12, color: 'var(--text-dimmer)', textAlign: 'center', marginTop: 40 }}>
              Start a conversation with {agentName}
            </p>
          )}
          {messages.map((msg, i) => (
            <div key={i} style={{ marginBottom: 12, display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '80%', padding: '10px 14px', borderRadius: 12,
                background: msg.role === 'user' ? 'rgba(0,212,170,0.15)' : 'rgba(0,0,0,0.05)',
                color: 'var(--text-primary)', fontSize: 13, lineHeight: 1.5,
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {msg.content}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div style={{ padding: '10px 14px', borderRadius: 12, background: 'rgba(0,0,0,0.05)', color: 'var(--text-dim)', fontSize: 13 }}>Thinking...</div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div style={{ padding: 12, borderTop: '1px solid var(--bg-border)', display: 'flex', gap: 8 }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder={`Ask ${agentName}...`}
            style={{
              flex: 1, padding: '12px 14px', background: 'rgba(0,0,0,0.03)',
              border: '1px solid var(--bg-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, outline: 'none',
            }}
          />
          <button onClick={sendMessage} disabled={loading} style={{
            padding: '12px 20px', background: loading ? 'var(--bg-border)' : 'var(--accent-good)', border: 'none',
            borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, cursor: loading ? 'default' : 'pointer',
          }}>Send</button>
        </div>
      </div>
    </div>
  );
}
